import React, { useState, useEffect } from 'react';
import {
    Box, Button, TextField, Typography, FormControl, InputLabel, Select, MenuItem, Stepper, Step,
    StepLabel, FormHelperText, InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import { postData } from '../../services/apiService';
import { useNavigate, Link } from 'react-router-dom';

const textFieldStyle = {
    "& .MuiOutlinedInput-root": {
        "& fieldset": { borderColor: "gray" },
        "&:hover fieldset": { borderColor: "white" },
        "&.Mui-focused fieldset": { borderColor: "white" },
    },
    "& .MuiInputBase-input": { color: "white" },
};

const InstitutionRegister = ({ executeRecaptcha }) => {
    const navigate = useNavigate();
    const [activeStep, setActiveStep] = useState(0);
    const [formData, setFormData] = useState({
        // Step 1 - Institution Details
        collegeName: '', institutionEmail: '', institutionPhone: '', address: '', pinCode: '',
        instituteType: '', establishmentYear: '', institutionPanCard: '', googleMapsLink: '',

        // Step 2 - Point of Contact Details
        firstName: '', email: '', phone: '', designation: '',
        adhaar: '', employee_id: '', password: '', confirmPassword: ''
    });

    const [errors, setErrors] = useState({});
    const [passwordStrength, setPasswordStrength] = useState('');
    const [showOTPDialog, setShowOTPDialog] = useState(false);
    const [otp, setOTP] = useState('');
    const [verificationError, setVerificationError] = useState('');
    const [otpExpired, setOtpExpired] = useState(false);
    const [resendDisabled, setResendDisabled] = useState(false);
    const [resendCountdown, setResendCountdown] = useState(0);

    const steps = ['Institution Details', 'Point of Contact Details'];

    const validateEmail = (email) => {
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return re.test(email);
    };

    const validatePhone = (phone) => {
        const re = /^\d{10,15}$/;
        return re.test(phone);
    };

    const validatePassword = (password) => {
        const re = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
        return re.test(password);
    };

    const checkPasswordStrength = (password) => {
        if (!password) return '';
        if (password.length < 8) return 'Weak (min 8 characters)';
        if (!validatePassword(password)) return 'Medium (needs uppercase, number, special char)';
        return 'Strong';
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Clear error when user types
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }

        // Check password strength in real-time
        if (name === 'password' || name === 'password') {
            setPasswordStrength(checkPasswordStrength(value));
        }
    };

    const validateStep1 = () => {
        const newErrors = {};

        if (!formData.collegeName) newErrors.collegeName = 'Institution name is required';
        if (!formData.institutionEmail) {
            newErrors.institutionEmail = 'Email is required';
        } else if (!validateEmail(formData.institutionEmail)) {
            newErrors.institutionEmail = 'Please enter a valid email address';
        }
        if (!formData.institutionPhone) {
            newErrors.institutionPhone = 'Phone number is required';
        } else if (!validatePhone(formData.institutionPhone)) {
            newErrors.institutionPhone = 'Please enter a valid phone number';
        }
        if (!formData.address) newErrors.address = 'Address is required';
        if (!formData.googleMapsLink) newErrors.googleMapsLink = 'Google Maps Link is required';
        if (!formData.pinCode) newErrors.pinCode = 'Pin code is required';
        if (!formData.instituteType) newErrors.instituteType = 'Institute type is required';
        if (!formData.establishmentYear) newErrors.establishmentYear = 'Year of establishment is required';
        if (!formData.institutionPanCard) newErrors.institutionPanCard = 'PAN card number is required';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validateStep2 = () => {
        const newErrors = {};

        if (!formData.firstName) newErrors.firstName = 'Name is required';
        if (!formData.email) {
            newErrors.email = 'Email is required';
        } else if (!validateEmail(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
        }
        if (!formData.phone) {
            newErrors.phone = 'Phone number is required';
        } else if (!validatePhone(formData.phone)) {
            newErrors.phone = 'Please enter a valid phone number';
        }
        if (!formData.designation) newErrors.designation = 'Designation is required';
        if (!formData.adhaar) newErrors.adhaar = 'Aadhaar number is required';
        if (!formData.employee_id) newErrors.employee_id = 'Enrollment Number (Usernmae) is required';
        if (!formData.password) {
            newErrors.password = 'Password is required';
        } else if (!validatePassword(formData.password)) {
            newErrors.password = 'Password must contain uppercase, lowercase, number, and special character';
        }
        if (!formData.confirmPassword) {
            newErrors.confirmPassword = 'Please confirm your password';
        } else if (formData.password !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleNext = () => {
        if (activeStep === 0 && validateStep1()) {
            console.log('formData', formData);
            setActiveStep(1);
        }
    };

    const handleBack = () => {
        setActiveStep(0);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (activeStep === 1 && validateStep2()) {
            try {
                const submitData = new FormData();
                Object.keys(formData).forEach(key => {
                    submitData.append(key, formData[key]);
                });
                const recaptchaValue = await executeRecaptcha('register');
                submitData.append('recaptchaValue', recaptchaValue);

                await postData('/register-institution', submitData);
                // setShowOTPDialog(true);
                navigate('/');
            } catch (error) {
                console.error('Registration failed:', error);
                if (error.response?.data?.msg) {
                    setErrors(prev => ({
                        ...prev,
                        submit: error.response.data.msg
                    }));
                }
            }
        }
    };

    // Add resend countdown timer
    useEffect(() => {
        let timer;
        if (resendCountdown > 0) {
            timer = setInterval(() => {
                setResendCountdown(prev => prev - 1);
            }, 1000);
        } else {
            setResendDisabled(false);
        }
        return () => clearInterval(timer);
    }, [resendCountdown]);

    const handleResendOTP = async () => {
        try {
            await postData('/resend-otp', { email: formData.email });
            setResendDisabled(true);
            setResendCountdown(60); // 60 seconds cooldown
            setOtpExpired(false);
            setVerificationError('');
        } catch (error) {
            setVerificationError(error.response?.data?.msg || 'Failed to resend OTP');
        }
    };

    const handleVerifyOTP = async () => {
        try {
            await postData('/verify-otp', {
                email: formData.email,
                otp
            });
            setShowOTPDialog(false);
            navigate('/');
        } catch (error) {
            const errorMsg = error.response?.data?.msg || 'OTP verification failed';
            setVerificationError(errorMsg);

            // Check if OTP is expired
            if (errorMsg.includes('expired')) {
                setOtpExpired(true);
            }
        }
    };

    const renderStep1 = () => (
        <Box component="form" noValidate sx={{ mt: 2 }}>
            <TextField label="Name of Institution" name="collegeName" value={formData.collegeName}
                onChange={handleChange} error={!!errors.collegeName} helperText={errors.collegeName}
                required fullWidth margin="normal" InputLabelProps={{ sx: { color: "lightgray" } }} sx={textFieldStyle}
            />

            <TextField label="Email of Institution" name="institutionEmail" type="email" value={formData.institutionEmail} onChange={handleChange}
                error={!!errors.institutionEmail} helperText={errors.institutionEmail} required fullWidth margin="normal"
                InputProps={{
                    endAdornment: (<InputAdornment position="end" sx={{ color: "lightgray" }}> </InputAdornment>
                    ),
                }} InputLabelProps={{ sx: { color: "lightgray" } }} sx={textFieldStyle}
            />

            <TextField label="Phone Number" name="institutionPhone" type="tel" value={formData.institutionPhone}
                onChange={handleChange} error={!!errors.institutionPhone} helperText={errors.institutionPhone} required
                fullWidth margin="normal" InputLabelProps={{ sx: { color: "lightgray" } }} sx={textFieldStyle}
            />

            <TextField label="Google Maps Link" name="googleMapsLink" type="tel" value={formData.googleMapsLink}
                onChange={handleChange} error={!!errors.googleMapsLink} helperText={errors.googleMapsLink} required
                fullWidth margin="normal" InputLabelProps={{ sx: { color: "lightgray" } }} sx={textFieldStyle}
            />

            <TextField label="Address" name="address" value={formData.address} onChange={handleChange} error={!!errors.address}
                helperText={errors.address} required fullWidth margin="normal" InputLabelProps={{ sx: { color: "lightgray" } }} sx={textFieldStyle}
            />

            <TextField label="Pin Code" name="pinCode" value={formData.pinCode} onChange={handleChange} error={!!errors.pinCode}
                helperText={errors.pinCode} required fullWidth margin="normal" InputLabelProps={{ sx: { color: "lightgray" } }} sx={textFieldStyle}
            />

            {/* <FormControl fullWidth margin="normal" required error={!!errors.instituteType}>
                <InputLabel sx={{ color: "lightgray" }}>Type of Institute</InputLabel>
                <Select name="instituteType" value={formData.instituteType} onChange={handleChange} label="Type of Institute" sx={textFieldStyle}>
                    <MenuItem value="Universitys">Universitys</MenuItem>
                    <MenuItem value="Campus">Campus</MenuItem>
                    <MenuItem value="School">School</MenuItem>
                </Select>
                {errors.instituteType && <FormHelperText error>{errors.instituteType}</FormHelperText>}
            </FormControl> */}

            <FormControl fullWidth margin="normal" required error={!!errors.instituteType}
                sx={{
                    "& .MuiOutlinedInput-root": {
                        "& fieldset": { borderColor: "lightgray" },
                        "&:hover fieldset": { borderColor: "white" },
                        "&.Mui-focused fieldset": { borderColor: "white" },
                    },
                    "& .MuiInputLabel-root": { color: "lightgray" },
                    "& .MuiSvgIcon-root": { color: "white" },
                }}
            >
                <InputLabel sx={{ color: "lightgray" }}>Type of Institute</InputLabel>
                <Select name="instituteType" value={formData.instituteType} onChange={handleChange} label="Type of Institute"
                    sx={{
                        color: "white", // Text color inside select
                    }}
                >
                    <MenuItem value="University">University</MenuItem>
                    <MenuItem value="College">College</MenuItem>
                    <MenuItem value="University Campus">University Campus</MenuItem>
                    <MenuItem value="University Campus">College Campus</MenuItem>
                </Select>
                {errors.instituteType && <FormHelperText error>{errors.instituteType}</FormHelperText>}
            </FormControl>

            {/* <TextField label="Year of Establishment" name="establishmentYear" type="month" value={formData.establishmentYear}
                onChange={handleChange} error={!!errors.establishmentYear} helperText={errors.establishmentYear} required
                fullWidth margin="normal" InputLabelProps={{ sx: { color: "lightgray" } }} sx={textFieldStyle} /> */}

            <TextField label="Year of Establishment" name="establishmentYear" type="month" value={formData.establishmentYear}
                onChange={handleChange} error={!!errors.establishmentYear} helperText={errors.establishmentYear} required
                fullWidth margin="normal" InputLabelProps={{ sx: { color: "lightgray" }, shrink: true }} sx={textFieldStyle} />

            <TextField label="Institution PAN Card" name="institutionPanCard" value={formData.institutionPanCard} onChange={handleChange}
                error={!!errors.institutionPanCard} helperText={errors.institutionPanCard} required fullWidth margin="normal"
                InputLabelProps={{ sx: { color: "lightgray" } }} sx={textFieldStyle}
            />
        </Box>
    );

    const renderStep2 = () => (
        <Box component="form" noValidate sx={{ mt: 2 }}>
            <TextField label="Name" name="firstName" value={formData.firstName}
                onChange={handleChange} error={!!errors.firstName} helperText={errors.firstName}
                required fullWidth margin="normal" InputLabelProps={{ sx: { color: "lightgray" } }} sx={textFieldStyle}
            />

            <TextField label="Email" name="email" type="email" value={formData.email} onChange={handleChange}
                error={!!errors.email} helperText={errors.email} required fullWidth margin="normal"
                InputProps={{ endAdornment: (<InputAdornment position="end" sx={{ color: "lightgray" }}>  </InputAdornment>), }}
                InputLabelProps={{ sx: { color: "lightgray" } }} sx={textFieldStyle}
            />

            <TextField label="Phone" name="phone" type="tel" value={formData.phone} onChange={handleChange}
                error={!!errors.phone} helperText={errors.phone} required fullWidth
                margin="normal" InputLabelProps={{ sx: { color: "lightgray" } }} sx={textFieldStyle}
            />

            <TextField label="Designation" name="designation" value={formData.designation} onChange={handleChange}
                error={!!errors.designation} helperText={errors.designation} required fullWidth
                margin="normal" InputLabelProps={{ sx: { color: "lightgray" } }} sx={textFieldStyle}
            />

            <TextField label="Aadhaar" name="adhaar" value={formData.adhaar} onChange={handleChange}
                error={!!errors.adhaar} helperText={errors.adhaar} required fullWidth
                margin="normal" InputLabelProps={{ sx: { color: "lightgray" } }} sx={textFieldStyle}
            />

            <TextField label="Employement ID" name="employee_id" value={formData.employee_id} onChange={handleChange}
                error={!!errors.employee_id} helperText={errors.employee_id} required fullWidth
                margin="normal" InputLabelProps={{ sx: { color: "lightgray" } }} sx={textFieldStyle}
            />

            <TextField label="Password" name="password" type="password" value={formData.password} onChange={handleChange} error={!!errors.password}
                helperText={errors.password || (formData.password ? `Password Strength: ${passwordStrength}` : "")}
                required fullWidth margin="normal" InputLabelProps={{ sx: { color: "lightgray" } }} sx={{
                    ...textFieldStyle, mt: 0, mb: 2,
                    '& .MuiFormHelperText-root': { color: errors.password ? undefined : 'white' }
                }}
            />

            <TextField label="Confirm Password" name="confirmPassword" type="password" value={formData.confirmPassword} onChange={handleChange}
                error={!!errors.confirmPassword} helperText={errors.confirmPassword} required fullWidth
                margin="normal" InputLabelProps={{ sx: { color: "lightgray" } }} sx={textFieldStyle}
            />
        </Box>
    );

    const renderOTPDialog = () => (
        <Dialog open={showOTPDialog} onClose={() => setShowOTPDialog(false)}>
            <DialogTitle>Email Verification</DialogTitle>
            <DialogContent>
                <Typography>
                    Please enter the OTP sent to your email ({formData.email})
                </Typography>
                <TextField
                    fullWidth
                    margin="normal"
                    label="OTP"
                    value={otp}
                    onChange={(e) => setOTP(e.target.value)}
                    error={!!verificationError}
                    helperText={verificationError}
                />
                {otpExpired && (
                    <Typography color="error" sx={{ mt: 1 }}>
                        OTP has expired. Please request a new one.
                    </Typography>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setShowOTPDialog(false)}>Cancel</Button>
                <Button
                    onClick={handleResendOTP}
                    disabled={resendDisabled}
                    sx={{ mr: 1 }}
                >
                    {resendDisabled ? `Resend in ${resendCountdown}s` : 'Resend OTP'}
                </Button>
                <Button
                    onClick={handleVerifyOTP}
                    variant="contained"
                    disabled={otpExpired}
                >
                    Verify
                </Button>
            </DialogActions>
        </Dialog>
    );

    return (
        <Box sx={{ width: '100%' }}>
            <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
                {steps.map((label) => (
                    <Step key={label}>
                        <StepLabel
                            sx={{
                                color: "white",
                                '& .MuiStepLabel-label': {
                                    color: "white !important"
                                },
                                '& .Mui-active .MuiStepLabel-label': {
                                    color: "white !important"
                                },
                                '& .Mui-completed .MuiStepLabel-label': {
                                    color: "white !important"
                                }
                            }}
                        >
                            {label}
                        </StepLabel>
                    </Step>
                ))}
            </Stepper>

            {activeStep === 0 ? renderStep1() : renderStep2()}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                <Button disabled={activeStep === 0} onClick={handleBack} sx={{ color: 'white' }} > Back </Button>
                <Button variant="contained" onClick={activeStep === steps.length - 1 ? handleSubmit : handleNext}
                    sx={{ bgcolor: "#3366FF", color: "white", "&:hover": { bgcolor: "#2952CC" }, }} >
                    {activeStep === steps.length - 1 ? 'Register' : 'Next'}
                </Button>
            </Box>

            {errors.submit && (
                <Typography color="error" sx={{ mt: 2, textAlign: 'center' }}>
                    {errors.submit}
                </Typography>
            )}

            {renderOTPDialog()}

            <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Typography variant="body2" sx={{ color: 'lightgray' }}>
                    Have already an account?{' '}
                    <Link to="/" style={{ color: '#3366FF', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                        Login here
                    </Link>
                </Typography>
            </Box>

        </Box>

    );
};

export default InstitutionRegister;
